'use client'

import { motion } from 'framer-motion'
import Link from 'next/link'

/**
 * ExposureProfile — the single product-story section of the homepage.
 *
 * July 2026 editorial pass: this replaces two overlapping sections
 * (HowItWorksTimeline + WhatHappensAfter) that explained the same journey
 * twice, once as a zig-zag timeline with illustrated mock-visuals and once
 * as a 4-card gamification grid. Visitors who reached the second one had
 * already understood the product; the repetition read as padding, and the
 * card grid made a research-adjacent product look like a game.
 *
 * The replacement is one continuous narrative:
 *   1. four numbered rows (Scan → Analyze → Track → Act) with hairline
 *      rules, matching the HowTo JSON-LD in page.tsx word for word
 *   2. one large exposure-trend chart, the clearest expression of the
 *      long-term value, sitting directly on the background (no card)
 *   3. one restrained paragraph for the habit system with inline text
 *      links to the feature pages (Passport / MicroQuests / Challenges),
 *      so the homepage still feeds those pages without four boxes
 */

const STEPS = [
  {
    title: 'Scan',
    description:
      'Point your camera at any everyday product, from bottled water to baby bottles. The AI identifies packaging type, brand, and category instantly.',
  },
  {
    title: 'Analyze',
    description:
      'Advanced algorithms cross-reference scientific data to estimate microplastic exposure and assign a 0–100 risk score.',
  },
  {
    title: 'Track',
    description:
      'Build a picture of your exposure over time. See which days, products, and categories contribute most.',
  },
  {
    title: 'Act',
    description:
      'Get personalized, evidence-based recommendations to reduce your microplastic intake starting today.',
  },
]

// Descending exposure index (the desired direction; lower is safer).
const POINTS = [82, 78, 74, 71, 65, 58, 52, 48]
const SWAP_INDEX = 4 // the "switched bottled water routine" moment

function TrendChart() {
  const W = 720
  const H = 180
  const PAD = 8
  const max = 90
  const min = 40
  const x = (i: number) => PAD + (i / (POINTS.length - 1)) * (W - PAD * 2)
  const y = (v: number) => PAD + (1 - (v - min) / (max - min)) * (H - PAD * 2)
  const path = POINTS.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')
  const areaPath = `${path} L${x(POINTS.length - 1)},${H} L${x(0)},${H} Z`

  return (
    <figure aria-label="Example exposure index falling from 82 to 48 over eight weeks">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img">
        <defs>
          <linearGradient id="profileTrendFade" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#1E6B47" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#1E6B47" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#profileTrendFade)" />
        <motion.path
          d={path}
          fill="none"
          stroke="#1E6B47"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 1.6, ease: 'easeOut' }}
        />
        {/* The swap annotation: a marker on the week the routine changed.
            SVG text scales with the viewBox, so at phone widths 12px units
            render around 6px — illegible. The in-chart label is desktop-only;
            mobile gets the same fact as the figcaption line below. */}
        <line
          x1={x(SWAP_INDEX)}
          y1={y(POINTS[SWAP_INDEX]) - 34}
          x2={x(SWAP_INDEX)}
          y2={y(POINTS[SWAP_INDEX]) - 8}
          stroke="#152319"
          strokeOpacity="0.25"
          strokeWidth="1"
          className="hidden sm:inline"
        />
        <text
          x={x(SWAP_INDEX)}
          y={y(POINTS[SWAP_INDEX]) - 42}
          textAnchor="middle"
          className="fill-ink hidden sm:inline"
          fontSize="12"
          fontWeight="600"
        >
          Switched bottled water routine
        </text>
        {/* Mobile-visible marker dot where the swap happened. */}
        <circle
          cx={x(SWAP_INDEX)}
          cy={y(POINTS[SWAP_INDEX])}
          r={6}
          fill="none"
          stroke="#152319"
          strokeOpacity="0.35"
          strokeWidth="1.5"
          className="sm:hidden"
        />
        {POINTS.map((v, i) => (
          <circle
            key={i}
            cx={x(i)}
            cy={y(v)}
            r={i === POINTS.length - 1 ? 5 : 3}
            fill="#1E6B47"
          />
        ))}
      </svg>
      <figcaption className="mt-2 text-xs sm:text-sm text-gray-500">
        <span className="flex justify-between">
          <span>8 weeks ago</span>
          <span className="font-semibold text-forest">&minus;41% exposure index</span>
        </span>
        <span className="block sm:hidden mt-1 text-gray-500">
          The circled week: switched bottled water routine.
        </span>
      </figcaption>
    </figure>
  )
}

export default function ExposureProfile() {
  return (
    <section
      className="w-full bg-cream py-14 sm:py-section"
      aria-labelledby="exposure-profile-heading"
    >
      <div className="content">
        <motion.div
          className="max-w-2xl mb-12 md:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-forest mb-3">
            After your first scan
          </p>
          <h2
            id="exposure-profile-heading"
            className="font-display text-[clamp(1.75rem,4.5vw,2.75rem)] font-bold text-ink leading-[1.12] mb-4"
          >
            An exposure profile that gets sharper every week.
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
            One scan answers one question. The value compounds when the scans
            add up: every product you check feeds a personal exposure index,
            and the app learns which rooms, routines, and categories drive
            yours.
          </p>
        </motion.div>

        {/* The four steps as numbered editorial rows. Copy matches the
            HowTo JSON-LD in page.tsx exactly; edit both together. */}
        <ol className="mb-16 md:mb-20">
          {STEPS.map((step, i) => (
            <motion.li
              key={step.title}
              className="grid grid-cols-[2.5rem_1fr] sm:grid-cols-[4rem_10rem_1fr] gap-x-4 sm:gap-x-6 border-t border-ink/10 py-6 sm:py-7"
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
            >
              <span
                aria-hidden="true"
                className="font-mono text-sm text-forest/60 pt-1 tracking-wider"
              >
                0{i + 1}
              </span>
              <h3 className="text-lg sm:text-xl font-bold text-ink leading-snug">
                {step.title}
              </h3>
              <p className="col-span-2 sm:col-span-1 mt-2 sm:mt-0 text-base sm:text-base text-gray-600 leading-relaxed">
                {step.description}
              </p>
            </motion.li>
          ))}
        </ol>

        {/* The outcome: one large chart, no card, no container. */}
        <motion.div
          className="mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5 }}
        >
          <h3 className="font-display text-2xl sm:text-3xl font-bold text-ink mb-3">
            See whether your changes are working.
          </h3>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-2xl mb-8 sm:mb-10">
            Every saved scan updates your exposure index, and lower is safer.
            When you swap a product or change a routine, the line shows
            whether it mattered.
          </p>
          <TrendChart />
        </motion.div>

        {/* The habit system, kept deliberately quiet: one paragraph with
            inline links instead of four feature cards. */}
        <motion.p
          className="border-t border-ink/10 pt-6 text-base sm:text-base text-gray-600 leading-relaxed max-w-2xl"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          Along the way, each real discovery earns a stamp in your{' '}
          <Link
            href="/features/plastic-passport"
            className="font-semibold text-forest underline underline-offset-4 decoration-forest/30 hover:decoration-forest transition-colors"
          >
            Plastic Passport
          </Link>
          , guided{' '}
          <Link
            href="/features/microquests"
            className="font-semibold text-forest underline underline-offset-4 decoration-forest/30 hover:decoration-forest transition-colors"
          >
            MicroQuests
          </Link>{' '}
          walk you through the kitchen and bathroom shelf, and a fresh{' '}
          <Link
            href="/features/weekly-challenges"
            className="font-semibold text-forest underline underline-offset-4 decoration-forest/30 hover:decoration-forest transition-colors"
          >
            weekly challenge
          </Link>{' '}
          lands every Monday. They exist for one reason: the second scan is
          when the app starts working for you.
        </motion.p>
      </div>
    </section>
  )
}
